import { Link } from "react-router-dom";
import { useState } from "react";
import {
  Home as HomeIcon,
  Utensils,
  ShowerHead,
  Brain,
  Shirt,
  GraduationCap,
  Mic2,
  HandHeart,
  Briefcase,
  Moon,
  Plus,
  Minus,
  ArrowUpRight,
} from "lucide-react";
import { useReveal } from "@/lib/useReveal";

const RIPPLE = "https://static.prod-images.emergentagent.com/jobs/7c3bd71d-f3a1-4475-868e-2031ff56ad92/images/0da293f3e2da169b65ef1706c0ba1a148d5f9fb022de9609c007727662d20643.png";

const SERVICES = [
  {
    icon: HomeIcon,
    title: "Short-Term Shelter & Crisis Accommodation",
    audience: "Women, children and young people",
    summary:
      "Filling the gap left by government housing — secure, confidential, immediate.",
    points: [
      "Crisis refuges — short-term accommodation, confidential and immediate",
      "Night shelters and \"sleep rooms\" — safe indoor spaces for rough sleepers",
      "Transitional housing while caseworkers help secure permanent housing",
    ],
  },
  {
    icon: Utensils,
    title: "Food & Nutritional Support",
    audience: "Dignified, low-barrier",
    summary:
      "Dignified food programs for those facing constant food insecurity.",
    points: [
      "Drop-in meals through community kitchens and engagement hubs",
      "Emergency food hampers and grocery vouchers",
      "Mobile food vans delivering meals to parks",
    ],
  },
  {
    icon: ShowerHead,
    title: "\"People Laundry\" & Personal Hygiene",
    audience: "Dignity services",
    summary:
      "Maintaining hygiene supports physical health, mental well-being and self-esteem.",
    points: [
      "Mobile and on-site laundry, free of charge",
      "Safe shower facilities stocked with toiletries and hygiene products",
      "Material aid — underwear, socks, blankets, sleeping bags and weather clothing",
    ],
  },
  {
    icon: Brain,
    title: "Mental Health & Psychosocial Support",
    audience: "Bridging gaps in public healthcare",
    summary:
      "Specialist support that holds complex trauma, mental illness and substance use together.",
    points: [
      "Trauma-informed casework with specialist case managers",
      "Dual-diagnosis counselling for trauma and substance use",
      "Safe spaces and women-only lounges",
      "Medical and psychiatric referrals",
    ],
  },
  {
    icon: Shirt,
    title: "Free Clothing",
    audience: "Women and people experiencing shortages",
    summary: "Clean, weather-appropriate clothing — no cost, no referral required.",
    points: [
      "Free clothing for those experiencing shortages",
      "Hygiene products and material aid available alongside",
    ],
  },
  {
    icon: GraduationCap,
    title: "Life Skills Development",
    audience: "30-minute sessions",
    summary: "Practical workshops that build everyday skills and confidence.",
    points: [
      "Communication and social skills",
      "Goal setting and leadership",
      "Conflict resolution",
      "Decision-making and personal responsibility",
    ],
  },
  {
    icon: Mic2,
    title: "Guest Speaker Sessions",
    audience: "Fortnightly",
    summary: "Role models and lived-experience speakers who inspire change.",
    points: [
      "Role models from sport, business, arts and community sectors",
      "Lived-experience speakers who inspire behavioural change",
    ],
  },
  {
    icon: HandHeart,
    title: "Mentorship & Case Support",
    audience: "Ongoing partnership",
    summary: "Individualised support that follows the person, not the program.",
    points: [
      "Individualised support from youth workers and mentors",
      "Goal planning, wellbeing check-ins and service referrals",
    ],
  },
  {
    icon: Briefcase,
    title: "Education & Employment Pathways",
    audience: "Re-engagement & opportunity",
    summary:
      "Reducing the barriers that lead people back into the same environments they came from.",
    points: [
      "Re-engagement with school or training",
      "Support into part-time work, traineeships and apprenticeships",
      "Transport assistance to reduce access barriers",
    ],
  },
  {
    icon: Moon,
    title: "After-Hours Engagement",
    audience: "Young people",
    summary: "Structured evening activities for safe, positive social interaction.",
    points: [
      "Structured evening activities to reduce boredom and risk behaviours",
      "Safe, supervised spaces that promote positive social interaction",
    ],
  },
];

function ServiceCard({ s, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const Icon = s.icon;
  return (
    <article
      data-testid={`service-card-${s.title.toLowerCase().replace(/\s+/g, "-").replace(/[^\w-]/g, "")}`}
      className="group rounded-3xl border border-slate-100 bg-white p-9 sm:p-10 transition-all duration-500 hover:border-[#2E8B8B]/40"
    >
      <div className="flex items-start justify-between gap-4">
        <span className="grid h-12 w-12 place-items-center rounded-2xl bg-[#E6F0F2] text-[#2E8B8B]">
          <Icon className="h-5 w-5" strokeWidth={1.6} />
        </span>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={open ? "Collapse" : "Expand"}
          className="grid h-10 w-10 place-items-center rounded-full border border-slate-200 text-slate-500 hover:border-[#2E8B8B] hover:text-[#2E8B8B] transition-colors"
          data-testid={`service-toggle-${s.title.toLowerCase().replace(/\s+/g, "-").replace(/[^\w-]/g, "")}`}
        >
          {open ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
        </button>
      </div>

      <p className="mt-7 text-xs uppercase tracking-[0.26em] text-slate-500">{s.audience}</p>
      <h3 className="font-heading mt-3 text-2xl leading-snug text-[#0A2540]">{s.title}</h3>
      <p className="mt-4 text-[15px] text-slate-600 leading-relaxed">{s.summary}</p>

      <div
        className={`grid transition-all duration-500 ${
          open ? "grid-rows-[1fr] opacity-100 mt-6" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          <ul className="space-y-2.5 pt-2 border-t border-slate-100">
            {s.points.map((p) => (
              <li key={p} className="flex items-start gap-3 text-sm text-slate-600 leading-relaxed pt-2.5">
                <span className="mt-2 h-1 w-1 rounded-full bg-[#2E8B8B] flex-shrink-0" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </article>
  );
}

export default function Services() {
  useReveal();
  return (
    <div data-testid="services-page">
      {/* HERO */}
      <section className="ripple-bg">
        <div className="container-app pt-24 sm:pt-32 pb-16">
          <div className="grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8 reveal">
              <p className="eyebrow">Our services</p>
              <h1 className="font-heading mt-6 text-[44px] sm:text-[64px] lg:text-[76px] leading-[0.98] tracking-[-0.02em] font-semibold text-[#0A2540]">
                The services we provide.
              </h1>
            </div>
            <div className="lg:col-span-4 reveal">
              <p className="text-lg text-slate-600 leading-relaxed">
                A holistic, trauma-informed response — from short-term shelter and food,
                to hygiene, mental health support, mentorship, and pathways into education
                and employment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* NO WRONG DOOR BANNER — minimal */}
      <section className="container-app pb-20">
        <div className="rounded-[2rem] border border-slate-100 p-8 sm:p-10 reveal grid sm:grid-cols-12 gap-6 items-center bg-white">
          <div className="sm:col-span-3">
            <p className="eyebrow text-[#2E8B8B]">"No Wrong Door"</p>
            <p className="font-heading mt-2 text-xl text-[#0A2540]">
              One front door.
            </p>
          </div>
          <div className="sm:col-span-9">
            <p className="text-base text-slate-600 leading-relaxed">
              When a woman walks into our hub just to use a washing machine or grab a sandwich,
              she is met with non-judgmental staff. That single interaction often opens the
              door to mental health care, legal support, and permanent housing.
            </p>
          </div>
        </div>
      </section>

      {/* SERVICES GRID — expandable cards */}
      <section className="container-app pb-24">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {SERVICES.map((s, i) => (
            <div key={s.title} className="reveal" style={{ transitionDelay: `${(i % 6) * 60}ms` }}>
              <ServiceCard s={s} defaultOpen={i === 0} />
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="container-app pb-24">
        <div className="relative overflow-hidden rounded-[2.5rem] border border-slate-100 bg-white p-12 sm:p-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="absolute -right-10 -top-10 h-64 w-64 opacity-[0.08] pointer-events-none" aria-hidden>
            <img src={RIPPLE} alt="" className="h-full w-full object-contain" />
          </div>
          <div className="lg:col-span-7 relative">
            <p className="eyebrow">Get involved</p>
            <h2 className="font-heading mt-5 text-3xl sm:text-4xl lg:text-5xl text-[#0A2540] leading-tight tracking-tight">
              Support a specific service.
            </h2>
            <p className="mt-5 text-base text-slate-600 max-w-xl leading-relaxed">
              Volunteer with a program or direct your donation to the service closest to your heart.
            </p>
          </div>
          <div className="lg:col-span-5 relative flex flex-wrap gap-4 lg:justify-end">
            <Link to="/volunteer" data-testid="services-volunteer-cta" className="btn-primary">
              Volunteer <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link to="/donate" data-testid="services-donate-cta" className="btn-outline">
              Donate
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
